import React, { createContext, useCallback, useContext, useState } from "react";
import {
  getShipInfoPaginated,
  getShipInfoById,
  getShipInfoShipped,
  getShipInfo,
  cancelShippingRequest
} from "../../services/shipinfoService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import { ShipInfo } from '../../types/shipinfo';
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";
import { ShippingData } from "../../pages/ShipInfo/components/ShippingInfoModal";

interface ShipInfoContextProps {
  apiResponse: ApiResponse<ShipInfo> | null;
  shipInfos: ShipInfo[] | null;
  shipInfoById: ShipInfo | null;
  loading: boolean;
  error: string | null;
  fetchShipInfos: (paginacao: Paginacao, filters?: any) => Promise<void>;
  fetchShipInfoById: (id: number) => Promise<ShipInfo | null>;
  markShipInfoAsShipped: (id: number, data: ShippingData) => Promise<void>;
  cancelShipping: (id: number) => Promise<void>;
}

interface ShipInfoProviderProps {
  children: React.ReactNode;
}

const ShipInfoContext = createContext<ShipInfoContextProps | null>(null);

const ShipInfoProvider: React.FC<ShipInfoProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<ShipInfo> | null>(null);
  const [shipInfos, setShipInfos] = useState<ShipInfo[] | null>(null);
  const [shipInfoById, setShipInfoById] = useState<ShipInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<Paginacao>({
    pageNum: 0,
    quantityPerPage: 20
  });

  const fetchShipInfos = useCallback(async (pag: Paginacao, filters: any = {}) => {
    setError(null);
    setLoading(true);

    try {
      const { campo, valor, ...otherFilters } = filters;
      const finalFilters = { ...otherFilters };

      if (campo && valor) {
        finalFilters.campo = campo;
        finalFilters.valor = valor;
      }

      const data = await getShipInfo( finalFilters);
      setShipInfos(data.data.values);
      setApiResponse(data);
      setPaginacao(pag); // Atualiza a paginação usada
    } catch (err) {
      console.error("Error fetching ship infos:", err);
      setError(getErrorMessage("fetchShipInfosError"));
      setShipInfos(null);
      setApiResponse(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchShipInfoById = useCallback(async (id: number) => {
    setError(null);
    setLoading(true);

    try {
      const response = await getShipInfoById(id);
      setShipInfoById(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching ship info by ID:", err);
      setError(getErrorMessage("fetchShipInfoByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

   const markShipInfoAsShipped = useCallback(async (id: number, data: ShippingData) => {
      setError(null);
      setLoading(true);

      try {
        await getShipInfoShipped(id, data);
        await fetchShipInfos(paginacao);
      } catch (err) {
        console.error("Error marking as shipped:", err);
        setError(getErrorMessage("markShipInfoAsShippedError"));
      } finally {
        setLoading(false);
      }
    }, [fetchShipInfos, paginacao]);

    const cancelShipping = useCallback(async (id: number) => {
      setError(null);
      setLoading(true);

      try {
        await cancelShippingRequest(id); // chama a função no serviço
        await fetchShipInfos(paginacao); // atualiza a lista após cancelar
      } catch (err) {
        console.error("Erro ao cancelar o shipping:", err);
        setError(getErrorMessage("cancelShippingError"));
      } finally {
        setLoading(false);
      }
    }, [fetchShipInfos, paginacao]);

  return (
    <ShipInfoContext.Provider
      value={{
        apiResponse,
        shipInfos,
        shipInfoById,
        loading,
        error,
        fetchShipInfos,
        fetchShipInfoById,
        markShipInfoAsShipped,
        cancelShipping
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </ShipInfoContext.Provider>
  );
};

export default ShipInfoProvider;

export const useShipInfo = (): ShipInfoContextProps => {
  const context = useContext(ShipInfoContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: "useShipInfo",
        providerName: "ShipInfoProvider",
      })
    );
  }
  return context;
};
